import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import styles from './LogoEditModal.module.css';
import '../styles/buttons.css';
import { SingleSelectDropdown } from './common/SingleSelectDropdown';
import { useConfirmDialog } from './common/useConfirmDialog';

// BrandLogo の正本は features/brands/types.ts
import type { BrandLogo } from '../features/brands/types';

interface LogoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
  brandLogo: BrandLogo | null;
}

const BACKGROUND_OPTIONS = [
  { value: '', label: '指定なし' },
  { value: 'white_back', label: '白バック' },
  { value: 'black_back', label: '黒バック' },
  { value: 'transparent', label: '透過' },
];

const COLOR_TYPE_OPTIONS = [
  { value: '', label: '指定なし' },
  { value: 'full_color', label: 'フルカラー' },
  { value: 'monochrome', label: 'モノクロ（黒）' },
  { value: 'white', label: '白抜き' },
  { value: 'solid_color', label: '単色（ブランドカラー）' },
  { value: 'grayscale', label: 'グレースケール' },
  { value: 'gradient', label: 'グラデーション' },
  { value: 'flat', label: 'フラット' },
];

const LAYOUT_OPTIONS = [
  { value: '', label: '指定なし' },
  { value: 'horizontal', label: '横組み' },
  { value: 'vertical', label: '縦組み' },
  { value: 'square', label: 'スクエア' },
  { value: 'logotype', label: 'ロゴタイプのみ' },
  { value: 'symbol', label: 'シンボル' },
  { value: 'lockup', label: 'アイコン+ロゴ' },
  { value: 'app_icon', label: 'アプリアイコン' },
  { value: 'circle', label: 'サークル' },
];

export default function LogoEditModal({
  isOpen,
  onClose,
  brandId,
  brandLogo,
}: LogoEditModalProps) {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirmDialog();

  const [background, setBackground] = useState<string>('');
  const [colorType, setColorType] = useState<string>('');
  const [layout, setLayout] = useState<string>('');
  const [isDefault, setIsDefault] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // brandLogoが変わったらフォームを初期化
  useEffect(() => {
    if (brandLogo) {
      setBackground(brandLogo.background || '');
      setColorType(brandLogo.color_type || '');
      setLayout(brandLogo.layout || '');
      setIsDefault(brandLogo.is_default || false);
      setDescription(brandLogo.description || '');
      setError(null);
    }
  }, [brandLogo]);

  // 更新mutation
  const updateMutation = useMutation({
    mutationFn: async (data: {
      background?: string;
      color_type?: string;
      layout?: string;
      is_default: boolean;
      description?: string;
    }) => {
      const response = await apiClient.patch(
        `/brands/${brandId}/logos/${brandLogo?.id}`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand', brandId] });
      onClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || err?.message || '更新に失敗しました');
    },
  });

  // 削除mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete(
        `/brands/${brandId}/logos/${brandLogo?.id}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand', brandId] });
      onClose();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || err?.message || '削除に失敗しました');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    updateMutation.mutate({
      background: background || undefined,
      color_type: colorType || undefined,
      layout: layout || undefined,
      is_default: isDefault,
      description: description || undefined,
    });
  };

  const handleDelete = async () => {
    // ブラウザの confirm は出ない環境があるので独自モーダルで確認する
    if (await confirm({ title: 'このロゴを削除しますか？', message: 'ブランドからこのロゴを外します（ロゴの画像自体は削除されません）。', confirmLabel: '削除する' })) {
      deleteMutation.mutate();
    }
  };

  const isLoading = updateMutation.isPending || deleteMutation.isPending;

  if (!isOpen || !brandLogo) return null;

  const logoUrl = brandLogo.logos?.url;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー */}
        <div className={styles.header}>
          <h2 className={styles.title}>ロゴを編集</h2>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {/* 2カラムレイアウト */}
        <div className={styles.mainContent}>
          {/* 左カラム: ロゴプレビュー */}
          <div
            className={styles.previewColumn}
            style={{
              background: background === 'transparent' || colorType === 'white'
                ? 'repeating-conic-gradient(rgba(255, 255, 255, 0.08) 0% 25%, rgba(0, 0, 0, 0.08) 0% 50%) 50% / 16px 16px'
                : undefined,
            }}
          >
            {logoUrl ? (
              <div className={styles.previewFull}>
                <img src={logoUrl} alt="ロゴプレビュー" />
              </div>
            ) : (
              <div className={styles.previewPlaceholder}>
                プレビューなし
              </div>
            )}
          </div>

          {/* 右カラム: フォーム */}
          <div className={styles.formColumn}>
            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}

              {/* 背景タイプ */}
              <div className={styles.formGroup}>
                <label className={styles.label}>背景</label>
                <SingleSelectDropdown
                  label="背景"
                  labelOnly
                  alwaysShowSelected
                  value={background}
                  onChange={setBackground}
                  options={BACKGROUND_OPTIONS}
                  disabled={isLoading}
                />
              </div>

              {/* 色タイプ */}
              <div className={styles.formGroup}>
                <label className={styles.label}>カラー</label>
                <SingleSelectDropdown
                  label="カラー"
                  labelOnly
                  alwaysShowSelected
                  value={colorType}
                  onChange={setColorType}
                  options={COLOR_TYPE_OPTIONS}
                  disabled={isLoading}
                />
              </div>

              {/* レイアウト */}
              <div className={styles.formGroup}>
                <label className={styles.label}>レイアウト</label>
                <SingleSelectDropdown
                  label="レイアウト"
                  labelOnly
                  alwaysShowSelected
                  value={layout}
                  onChange={setLayout}
                  options={LAYOUT_OPTIONS}
                  disabled={isLoading}
                />
              </div>

              {/* 説明 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>説明</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={styles.textarea}
                  rows={2}
                  placeholder="このロゴの用途や説明..."
                  disabled={isLoading}
                />
              </div>

              {/* デフォルト設定 */}
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span>デフォルトとして設定</span>
                </label>
              </div>
            </form>
          </div>
        </div>

        {/* フッター */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <button
              type="button"
              className="btn-modal-danger"
              onClick={() => void handleDelete()}
              disabled={isLoading}
            >
              <img src="/bin_1.svg" alt="" aria-hidden="true" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
              {deleteMutation.isPending ? '削除中...' : '削除'}
            </button>
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className="btn-modal"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {updateMutation.isPending ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>
      {dialog}
    </div>,
    document.body
  );
}
